import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";

const LINES = ["VISION.", "CULTURE.", "PROGRESS."];

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.5 + i * 0.16 },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero" className="relative min-h-screen w-full overflow-hidden flex flex-col justify-end pb-16 md:pb-20">
      {/* Parallax background image */}
      <motion.div style={{ y: yBg }} className="absolute inset-0 -top-[10%] h-[120%] z-0">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40 grayscale-img"
          style={{ backgroundImage: "url('https://images.pexels.com/photos/19218008/pexels-photo-19218008.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
      </motion.div>

      {/* Top label */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute top-28 left-5 md:left-10 z-10 max-w-xs"
      >
        <p className="text-xs uppercase tracking-[0.24em] font-bold text-neutral-400">
          The Central Media Network — Fashion · Music · Art · Sports · Business · Culture
        </p>
      </motion.div>

      <motion.div style={{ y: yText, opacity }} className="relative z-10 px-5 md:px-10">
        <h1 className="font-display uppercase leading-[0.82] tracking-tighter text-[19vw] md:text-[15vw]">
          {LINES.map((line, i) => (
            <span key={line} className="line-mask">
              <motion.span
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="visible"
                className={`block ${i === 1 ? "text-[#E30613]" : "text-white"}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6, duration: 0.8 }}
          className="mt-8 flex flex-col md:flex-row md:items-end gap-6 md:gap-16 max-w-4xl"
        >
          <p className="text-base md:text-lg text-neutral-300 leading-relaxed max-w-md">
            VLMAG is the media network telling the stories of the creatives, brands, artists and
            entrepreneurs shaping the next generation of culture.
          </p>
          <a
            href="#networks"
            data-testid="hero-explore"
            className="group inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] font-bold text-white shrink-0"
          >
            <span className="w-12 h-12 border border-white/40 flex items-center justify-center group-hover:bg-[#E30613] group-hover:border-[#E30613] transition-colors duration-300">
              <ArrowDown size={18} className="group-hover:translate-y-0.5 transition-transform" />
            </span>
            Explore the Network
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
};
